<template>
  <div class="login-page">
    <aside class="login-page__hero">
      <AuthHero class="login-page__hero-bg" />

      <div class="login-page__hero-overlay">
        <header class="brand-top">
          <div class="brand-logo">
            <span class="brand-logo-mark" aria-hidden="true">
              <img src="/mcci_logo.png" alt="OMS" width="50" height="50" />
            </span>
            <span class="brand-logo-text">Team Manakkal</span>
          </div>
        </header>

        <QuoteCarousel :quotes="healthQuotes" class="brand-quote" />

        <footer class="brand-footer">
          <div class="brand-footer-mark" aria-hidden="true">
            <img src="/raymas-logo.png" alt="Raymas" width="30" height="30" />
          </div>
          <p class="brand-footer-desc">
            Raymas Technologies is a product-driven technology company focused on
            building scalable, intelligent, and industry-specific management software
            platforms.
          </p>
          <div class="brand-footer-meta">
            <a href="https://raymas.in" target="_blank" rel="noopener" class="brand-footer-link">RAYMAS.IN</a>
            <span class="brand-footer-divider">|</span>
            <span class="brand-footer-phone">+91-9207948134</span>
          </div>
        </footer>
      </div>
    </aside>

    <main class="login-page__form">
      <div class="login-card">
        <div class="login-eyebrow">
          <span class="login-eyebrow__rule" aria-hidden="true"></span>
          <span class="login-eyebrow__label">Sign in</span>
        </div>

        <h1 class="login-title">Welcome back.</h1>
        <p class="login-subtitle">Enter your credentials to continue.</p>

        <form class="login-form" @submit.prevent="login" method="POST" novalidate>
          <div v-if="showMessage" class="login-alert" role="alert">{{ message }}</div>

          <div class="form-field">
            <label for="login-email" class="form-label">Email</label>
            <input
              id="login-email"
              v-model.trim="email"
              type="email"
              name="email"
              class="form-input"
              placeholder="you@example.com"
              autocomplete="username"
              autocapitalize="off"
              autocorrect="off"
              spellcheck="false"
              inputmode="email"
              maxlength="254"
              required
            />
          </div>

          <div class="form-field">
            <label for="login-password" class="form-label">Password</label>
            <div class="form-input-wrap">
              <input
                id="login-password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                name="password"
                class="form-input form-input--with-toggle"
                placeholder="Enter your password"
                autocomplete="current-password"
                autocapitalize="off"
                autocorrect="off"
                spellcheck="false"
                maxlength="128"
                required
              />
              <button
                type="button"
                class="form-input-toggle"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                :aria-pressed="showPassword"
                @click="showPassword = !showPassword"
              >
                <span class="form-input-toggle__label">{{ showPassword ? 'Hide' : 'Show' }}</span>
              </button>
            </div>
          </div>

          <button
            type="submit"
            class="login-btn"
            :class="{ 'login-btn--busy': isSubmitting }"
            :disabled="isSubmitting"
          >
            <span v-if="isSubmitting" class="login-btn__spinner" aria-hidden="true"></span>
            <span class="login-btn__label">{{ isSubmitting ? 'Signing in' : 'Sign in' }}</span>
          </button>
        </form>
      </div>
    </main>

    <transition name="welcome-fade">
      <div v-if="showWelcome" class="welcome-splash" role="status" aria-live="polite">
        <div class="welcome-stage">
          <div class="welcome-burst" aria-hidden="true">
            <span class="welcome-ring welcome-ring--1"></span>
            <span class="welcome-ring welcome-ring--2"></span>
            <span class="welcome-orb">
              <svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <polyline class="welcome-check" points="5 13 10 18 19 7"/>
              </svg>
            </span>
          </div>
          <h1 class="welcome-title">
            <span class="welcome-title__lead">Welcome back</span>
            <template v-if="welcomeName">
              <span class="welcome-title__comma">,</span>
              <span class="welcome-title__name">{{ welcomeName }}</span>
            </template>
            <span class="welcome-title__bang">!</span>
          </h1>
          <p class="welcome-subtitle">Loading your dashboard…</p>
          <div class="welcome-progress" aria-hidden="true">
            <span class="welcome-progress__bar"></span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import axios from 'axios'
import posthog from 'posthog-js'
import AuthHero from './AuthHero.vue'
import QuoteCarousel from '@/components/QuoteCarousel.vue'
import { healthQuotes } from '@/data/healthQuotes'
import { getLandingForRoles } from '@/config/roleSidebarMap'
import { applyAuthUser, resetCompletion } from '@/utils/profileCompletion'

export default {
  name: 'Login',
  components: { AuthHero, QuoteCarousel },
  data() {
    return {
      email: '',
      password: '',
      showPassword: false,
      showMessage: false,
      message: '',
      healthQuotes,
      isSubmitting: false,
      showWelcome: false,
      welcomeName: '',
    }
  },
  beforeDestroy() {
    if (this._welcomeTimer) {
      clearTimeout(this._welcomeTimer)
      this._welcomeTimer = null
    }
  },
  methods: {
    clearAuthState() {
      localStorage.removeItem('api_token')
      localStorage.removeItem('roles')
      localStorage.removeItem('auth_user')
      resetCompletion()
      this.$store.commit('clearOrganisationContext')
      this.$store.commit('clearPermissions')
    },
    pickWelcomeName(user) {
      if (!user) return ''
      const name = typeof user.name === 'string' ? user.name.trim() : ''
      if (name) {
        const first = name.split(/\s+/)[0]
        return first || name
      }
      const email = typeof user.email === 'string' ? user.email.trim() : ''
      if (email) {
        return email.split('@')[0]
      }
      return ''
    },
    login() {
      if (this.isSubmitting) return
      const self = this
      self.showMessage = false
      self.message = ''
      self.isSubmitting = true

      axios.post(this.$apiAdress + '/api/login', {
        email: self.email,
        password: self.password,
      }).then(function (response) {
        const token = response.data.access_token
        const roles = Array.isArray(response.data.roles)
          ? response.data.roles.join(',')
          : (response.data.roles || '')

        self.email = ''
        self.password = ''
        localStorage.setItem('api_token', token)
        localStorage.setItem('roles', roles)
        self.$store.commit('setPermissions', response.data.permissions || {})

        if (response.data.user) {
          localStorage.setItem('auth_user', JSON.stringify(response.data.user))
          self.$store.commit('hydrateOrganisationFromAuthUser', response.data.user)
        }

        return axios.get(self.$apiAdress + '/api/auth/me')
      }).then(function (response) {
        localStorage.setItem('roles', response.data.roles.join(','))
        localStorage.setItem('auth_user', JSON.stringify(response.data))
        applyAuthUser(response.data)
        self.$store.commit('setPermissions', response.data.permissions || {})
        self.$store.commit('hydrateOrganisationFromAuthUser', response.data)

        if (response.data && response.data.id) {
          posthog.identify(String(response.data.id), {
            email: response.data.email,
            name: response.data.name,
            roles: response.data.roles,
          })
        }

        self.welcomeName = self.pickWelcomeName(response.data)
        self.showWelcome = true
        // Honour ?next=… from the redirect interceptor so users land
        // back on the page they were trying to view. Reject obviously
        // unsafe destinations (anything not starting with /) so a
        // crafted query string can't open-redirect off-site.
        const next = self.$route && self.$route.query && self.$route.query.next
        const safeNext = typeof next === 'string' && next.startsWith('/') && !next.startsWith('//')
          ? next
          : getLandingForRoles(localStorage.getItem('roles'))
        self._welcomeTimer = setTimeout(function () {
          self.$router.push({ path: safeNext })
        }, 2500)
      }).catch(function (error) {
        self.clearAuthState()
        self.password = ''
        self.showPassword = false
        self.message = self.resolveLoginErrorMessage(error)
        self.showMessage = true
        self.isSubmitting = false
      })
    },
    resolveLoginErrorMessage(error) {
      if (!error || !error.response) {
        return 'Cannot reach the server. Check your internet connection and try again.'
      }

      const { status, data } = error.response

      if (status === 419) {
        return 'Login failed due to an auth session mismatch. Refresh the page and try again.'
      }
      if (status === 401) {
        return (data && data.message) || 'Incorrect email or password.'
      }
      if (status === 422) {
        if (data && data.errors) {
          const first = Object.values(data.errors)[0]
          if (Array.isArray(first) && first.length) return first[0]
        }
        return (data && data.message) || 'Please enter a valid email and password.'
      }
      if (status === 429) {
        return 'Too many login attempts. Please wait a moment and try again.'
      }
      if (status === 503) {
        return (data && data.message) || 'Service is temporarily unavailable. Please try again in a few minutes.'
      }
      if (status >= 500) {
        return (data && data.message) || 'Something went wrong on our side. Please try again.'
      }
      return (data && data.message) || 'Login failed. Please try again.'
    }
  }
}
</script>

<style scoped>
.login-page {
  display: flex;
  flex-direction: row;
  min-height: 100vh;
  width: 100%;
  background: #ffffff;
  overflow: hidden;
}

/* ---------------- Left hero panel ---------------- */
.login-page__hero {
  position: relative;
  flex: 0 0 calc(50% + 130px);
  margin-right: -130px;
  z-index: 2;
  overflow: hidden;
}

.login-page__hero-bg {
  position: absolute;
  inset: 0;
  z-index: 0;
}

.login-page__hero-overlay {
  position: relative;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  min-height: 100vh;
  padding: 2.75rem 3rem;
  padding-right: calc(3rem + 130px); /* keep content off the wave strip area */
  color: #ffffff;
}

.brand-logo {
  display: inline-flex;
  align-items: center;
  gap: 0.65rem;
}

.brand-logo-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.brand-logo-text {
  font-size: 1.6rem;
  font-weight: 700;
  color: #ffffff;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.brand-quote {
  max-width: 32rem;
  min-height: 11rem;
  color: #ffffff;
}

@media (max-width: 1199px) {
  .brand-quote >>> .quote-carousel__text {
    font-size: 2rem;
  }
}

.brand-footer {
  max-width: 26rem;
}

.brand-footer-mark {
  margin-bottom: 1rem;
}

.brand-footer-desc {
  margin: 0 0 0.85rem;
  font-size: 0.78rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.82);
}

.brand-footer-meta {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.5px;
}

.brand-footer-link {
  color: #7ed957;
  text-decoration: none;
}

.brand-footer-link:hover {
  text-decoration: underline;
}

.brand-footer-divider {
  color: rgba(255, 255, 255, 0.4);
}

.brand-footer-phone {
  color: rgba(255, 255, 255, 0.88);
}

/* ---------------- Right form panel ---------------- */
.login-page__form {
  position: relative;
  flex: 0 0 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 2rem;
  background: #ffffff;
  z-index: 1;
}

.login-card {
  width: 100%;
  max-width: 22rem;
}

.login-eyebrow {
  display: flex;
  align-items: center;
  gap: 0.7rem;
  margin-bottom: 1.75rem;
}

.login-eyebrow__rule {
  width: 28px;
  height: 1px;
  background: #0e5b46;
}

.login-eyebrow__label {
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #0e5b46;
}

.login-title {
  margin: 0 0 0.5rem;
  font-family: 'Iowan Old Style', 'Apple Garamond', 'Baskerville', 'Times New Roman', 'Droid Serif', Times, serif;
  font-size: 2.05rem;
  font-weight: 500;
  letter-spacing: -0.015em;
  line-height: 1.15;
  color: #0d1b2a;
}

.login-subtitle {
  margin: 0 0 2.25rem;
  font-size: 0.9rem;
  color: #6b7280;
  line-height: 1.5;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.2rem;
}

.login-alert {
  margin-bottom: 0.25rem;
  padding: 0.65rem 0.85rem;
  border-left: 2px solid #c0394b;
  font-size: 0.83rem;
  color: #7d1c28;
  background: #fdf2f4;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.form-label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #6b7280;
}

.form-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.form-input {
  width: 100%;
  height: 44px;
  padding: 0;
  border: none;
  border-bottom: 1px solid #d8dde6;
  outline: none;
  background: transparent;
  font-size: 1rem;
  color: #0d1b2a;
  transition: border-color 0.2s ease;
}

.form-input--with-toggle {
  padding-right: 3.5rem;
}

.form-input:hover {
  border-bottom-color: #aab2c0;
}

.form-input:focus {
  border-bottom-color: #0e5b46;
}

.form-input:-webkit-autofill,
.form-input:-webkit-autofill:hover,
.form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #0d1b2a;
  box-shadow: 0 0 0 1000px #ffffff inset;
  transition: background-color 5000s ease-in-out 0s;
}

.form-input-toggle {
  position: absolute;
  right: 0;
  bottom: 12px;
  padding: 0;
  margin: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}

.form-input-toggle__label {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #9aa3b2;
  transition: color 0.18s ease;
}

.form-input-toggle:hover .form-input-toggle__label {
  color: #0e5b46;
}

.form-input-toggle:focus { outline: none; }
.form-input-toggle:focus-visible {
  outline: none;
  border-radius: 4px;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.22);
}

.login-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
  padding: 0.9rem 1rem;
  border: none;
  border-radius: 4px;
  background: #0e5b46;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.login-btn:hover:not(:disabled) {
  background: #0a4a3a;
}

.login-btn:active:not(:disabled) {
  background: #083728;
}

.login-btn:focus { outline: none; }
.login-btn::-moz-focus-inner { border: 0; }
.login-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(22, 160, 122, 0.28);
}

.login-btn:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

.login-btn--busy {
  cursor: progress;
}

.login-btn__spinner {
  width: 14px;
  height: 14px;
  margin-right: 0.6rem;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: login-spin 0.7s linear infinite;
}

@keyframes login-spin {
  to { transform: rotate(360deg); }
}

/* ---------------- Responsive ---------------- */
@media (max-width: 991px) {
  .login-page {
    flex-direction: column;
  }

  .login-page__hero {
    display: none;
  }

  .login-page__form {
    flex: 1 1 auto;
    width: 100%;
    padding: 3rem 1.5rem;
  }
}

@media (max-width: 575px) {
  .login-title {
    font-size: 1.75rem;
  }

  .login-page__form {
    padding: 2.25rem 1.25rem;
  }
}

/* ---------------- Welcome splash ---------------- */
.welcome-splash {
  position: fixed;
  inset: 0;
  z-index: 1500;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: linear-gradient(135deg, #0a4a3a 0%, #0e5b46 50%, #16a07a 100%);
  color: #ffffff;
  overflow: hidden;
}

.welcome-splash::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(circle at 20% 25%, rgba(255, 255, 255, 0.16), transparent 45%),
    radial-gradient(circle at 80% 75%, rgba(255, 255, 255, 0.10), transparent 50%);
  pointer-events: none;
  animation: welcome-glow 5s ease-in-out infinite alternate;
}

@keyframes welcome-glow {
  0%   { opacity: 0.7; transform: scale(1); }
  100% { opacity: 1;   transform: scale(1.06); }
}

.welcome-stage {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  z-index: 1;
}

.welcome-burst {
  position: relative;
  width: 120px;
  height: 120px;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.welcome-ring {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.55);
  transform: translate(-50%, -50%) scale(0.6);
  opacity: 0;
}

.welcome-ring--1 { animation: welcome-ring-pulse 2.2s ease-out 0.2s infinite; }
.welcome-ring--2 { animation: welcome-ring-pulse 2.2s ease-out 1.1s infinite; }

@keyframes welcome-ring-pulse {
  0%   { transform: translate(-50%, -50%) scale(0.6); opacity: 0.85; }
  80%  { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
}

.welcome-orb {
  position: relative;
  z-index: 2;
  width: 84px;
  height: 84px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  color: #0e5b46;
  box-shadow:
    0 8px 24px rgba(0, 0, 0, 0.18),
    inset 0 0 0 1px rgba(14, 91, 70, 0.08);
  animation: welcome-orb-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes welcome-orb-pop {
  0%   { transform: scale(0.3) rotate(-15deg); opacity: 0; }
  60%  { transform: scale(1.08) rotate(4deg);  opacity: 1; }
  100% { transform: scale(1) rotate(0);        opacity: 1; }
}

.welcome-check {
  stroke-dasharray: 32;
  stroke-dashoffset: 32;
  animation: welcome-check-draw 0.45s ease-out 0.35s forwards;
}

@keyframes welcome-check-draw {
  to { stroke-dashoffset: 0; }
}

.welcome-title {
  margin: 0;
  font-size: clamp(1.6rem, 3.6vw, 2.2rem);
  font-weight: 700;
  letter-spacing: -0.01em;
  color: #ffffff;
  line-height: 1.2;
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 0.25rem;
}

.welcome-title__lead,
.welcome-title__comma,
.welcome-title__name,
.welcome-title__bang {
  display: inline-block;
  opacity: 0;
  transform: translateY(8px);
  animation: welcome-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) forwards;
}

.welcome-title__lead  { animation-delay: 0.30s; }
.welcome-title__comma { animation-delay: 0.40s; }
.welcome-title__name  { animation-delay: 0.45s; color: #c9f5e3; }
.welcome-title__bang  { animation-delay: 0.55s; }

.welcome-subtitle {
  margin: 0.6rem 0 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.82);
  letter-spacing: 0.02em;
  opacity: 0;
  transform: translateY(8px);
  animation: welcome-rise 0.45s cubic-bezier(0.22, 1, 0.36, 1) 0.65s forwards;
}

@keyframes welcome-rise {
  to { opacity: 1; transform: translateY(0); }
}

.welcome-progress {
  position: relative;
  width: 220px;
  max-width: 60vw;
  height: 3px;
  margin-top: 1.6rem;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  overflow: hidden;
  opacity: 0;
  animation: welcome-fade-in 0.4s ease-out 0.7s forwards;
}

.welcome-progress__bar {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 35%;
  border-radius: 999px;
  background: linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, #ffffff 50%, rgba(255, 255, 255, 0) 100%);
  animation: welcome-progress-slide 1.1s ease-in-out 0.8s infinite;
}

@keyframes welcome-progress-slide {
  0%   { left: -40%; }
  100% { left: 100%; }
}

@keyframes welcome-fade-in {
  to { opacity: 1; }
}

.welcome-fade-enter-active,
.welcome-fade-leave-active {
  transition: opacity 0.3s ease;
}

.welcome-fade-enter,
.welcome-fade-leave-to {
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .welcome-splash::before,
  .welcome-ring,
  .welcome-orb,
  .welcome-check,
  .welcome-title__lead,
  .welcome-title__comma,
  .welcome-title__name,
  .welcome-title__bang,
  .welcome-subtitle,
  .welcome-progress,
  .welcome-progress__bar {
    animation: none !important;
  }
  .welcome-title__lead,
  .welcome-title__comma,
  .welcome-title__name,
  .welcome-title__bang,
  .welcome-subtitle,
  .welcome-progress {
    opacity: 1;
    transform: none;
  }
}
</style>
